import {
  applySnapshot,
  cast,
  flow,
  getSnapshot,
  Instance,
  types,
} from 'mobx-state-tree';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import moment from 'moment/moment';
import { GIS } from 'http-requests';
import { RequestStateModel } from './general-models';
import { formatDateToISO, killNulls } from 'utils';
import { IObject } from 'types';

const StatusesModel = types.model({
  checkGISAccess: types.optional(RequestStateModel, {}),
  GISGUIDS: types.optional(RequestStateModel, {}),
  postGISGUIDS: types.optional(RequestStateModel, {}),
  records: types.optional(RequestStateModel, {}),
});

const GUIDModel = types.model('GUIDModel', {
  id: types.optional(types.maybeNull(types.string), null),
  provider_id: types.optional(types.maybeNull(types.string), null),
  premises_id: types.optional(types.maybeNull(types.string), null),
  object_id: types.optional(types.maybeNull(types.string), null),
  tag: types.optional(types.maybeNull(types.string), null),
  desc: types.optional(types.maybeNull(types.string), null),
  gis: types.optional(types.maybeNull(types.string), null),
  root: types.optional(types.maybeNull(types.string), null),
  version: types.optional(types.maybeNull(types.string), null),
  unique: types.optional(types.maybeNull(types.string), null),
  number: types.optional(types.maybeNull(types.string), null),
  updated: types.optional(types.maybeNull(types.string), null),
  deleted: types.optional(types.maybeNull(types.string), null),
  saved: types.optional(types.maybeNull(types.string), null),
  record_id: types.optional(types.maybeNull(types.string), null),
  transport: types.optional(types.maybeNull(types.string), null),
  status: types.optional(types.maybeNull(types.string), null),
  error: types.optional(types.maybeNull(types.string), null),
});

const RecordModel = types
  .model('RecordModel', {
    acked: types.optional(types.maybeNull(types.string), null),
    cancelled: types.optional(types.maybeNull(types.string), null),
    child_id: types.optional(types.maybeNull(types.string), null),
    desc: types.optional(types.maybeNull(types.string), null),
    error: types.optional(types.maybeNull(types.string), null),
    follower_id: types.optional(types.maybeNull(types.string), null),
    fraction: types.optional(types.maybeNull(types.array(types.number)), null),
    generated_id: types.optional(types.string, ''),
    house_info: types.maybeNull(
      types.model('RecordHouseModel', {
        address: types.maybeNull(types.string),
        cadastral_number: types.maybeNull(types.string),
        fias_addrobjs: types.maybeNull(types.array(types.string)),
        gis_fias: types.maybeNull(types.string),
        gis_uid: types.maybeNull(types.string),
        id: types.maybeNull(types.string),
      })
    ),
    message_guid: types.optional(types.string, ''),
    operation: types.optional(types.string, ''),
    parent_id: types.optional(types.maybeNull(types.string), null),
    provider_info: types.maybeNull(
      types.model('RecordProviderModel', {
        crm_status: types.maybeNull(types.string),
        gis_online_changes: types.maybeNull(types.boolean),
        id: types.maybeNull(types.string),
        inn: types.maybeNull(types.string),
        ogrn: types.maybeNull(types.string),
        str_name: types.maybeNull(types.string),
      })
    ),
    restarts: types.optional(types.maybeNull(types.number), null),
    retries: types.optional(types.maybeNull(types.number), null),
    saved: types.optional(types.string, ''),
    sheduled: types.optional(types.maybeNull(types.string), null),
    stated: types.optional(types.maybeNull(types.string), null),
    status: types.optional(types.string, ''),
    stored: types.optional(types.maybeNull(types.string), null),
    trace: types.optional(types.maybeNull(types.string), null),
    task_state: types.optional(types.string, 'new'),
    task_owner: types.optional(types.maybeNull(types.string), null),
    warnings: types.optional(types.maybeNull(types.array(types.string)), null),
  })
  .volatile((self) => ({
    updateRecord: RequestStateModel.create({}),
    updateErrors: RequestStateModel.create({}),
    errorsList: null as {
      guid_errors: { error: string; id: string }[];
      warnings: string[];
      operation_error: string | null;
    } | null,
  }))
  .views((self) => ({
    get snapshot() {
      return getSnapshot(self);
    },
  }))
  .actions((self) => ({
    setRecordField<T extends keyof typeof self.snapshot>(
      key: T,
      value: (typeof self)[T]
    ) {
      self[key] = value;
    },
    getErrors: flow(function* () {
      try {
        self.updateErrors.loadingStates();
        const response = yield GIS.GetGISRecordErrors(self.generated_id);
        self.errorsList = response;
        self.updateErrors.successStates();

        return response;
      } catch (e) {
        self.updateErrors.errorStates();
        console.error(e);
      } finally {
        self.updateErrors.finallyStates();
      }
    }),
  }));

export type IRecord = Instance<typeof RecordModel>;

const RecordFiltersModel = types
  .model('RecordFiltersModel', {
    limit: types.number,
    offset: types.number,
    task_state__nin: types.array(types.string),
    status__ne: types.string,
    provider_id: types.maybeNull(types.string),
    provider_info__inn: types.maybeNull(types.string),
    saved__lte: types.maybeNull(types.Date),
    saved__gte: types.maybeNull(types.Date),
    house_id: types.maybeNull(types.string),
    operation: types.maybeNull(types.string),
    task_state: types.maybeNull(types.string),
    task_owner: types.maybeNull(types.string),
    status: types.maybeNull(types.string),
    provider_info__str_name__icontains: types.maybeNull(types.string),
  })
  .views((self) => ({
    get snapshot() {
      return getSnapshot(self);
    },
  }))
  .actions((self) => ({
    setFilter<T extends keyof typeof self.snapshot>(
      key: T,
      value: (typeof self)[T]
    ) {
      self[key] = value;
    },
    clearFilters() {
      applySnapshot(self, defaultFilters);
    },
  }));

export type IRecordFilters = Instance<typeof RecordFiltersModel>;

const RecordFilters = types.snapshotProcessor(RecordFiltersModel, {
  postProcessor: (self) => {
    return killNulls({
      ...self,
      saved__lte:
        (self.saved__lte &&
          formatDateToISO(moment(self.saved__lte).startOf('day'))) ||
        null,
      saved__gte:
        (self.saved__gte &&
          formatDateToISO(moment(self.saved__gte).endOf('day'))) ||
        null,
      task_state__nin: !!self.task_state ? null : self.task_state__nin,
      status__ne: !!self.status ? null : self.status__ne,
    });
  },
});

const defaultFilters: Pick<
  IRecordFilters,
  'limit' | 'offset' | 'task_state__nin' | 'status__ne'
> = {
  limit: 20,
  offset: 0,
  task_state__nin: cast(['done', 'restart']),
  status__ne: 'done',
};

const RecordsModel = types
  .model('Records', {
    filters: types.optional(RecordFilters, defaultFilters),
    totalCount: types.optional(types.number, 0),
    list: types.optional(types.array(RecordModel), []),
  })
  .volatile(() => ({
    searchFilters: null as IRecordFilters | null,
  }))
  .views((self) => ({
    get isIdentityFilters(): boolean {
      return (
        self.searchFilters !== null &&
        isEqual(self.filters.snapshot, {
          ...self.searchFilters.snapshot,
          ...defaultFilters,
        })
      );
    },
  }))
  .actions((self) => ({
    clearList() {
      applySnapshot(self.list, []);
      self.totalCount = 0;
    },
    saveSearchFilters() {
      self.searchFilters = RecordFilters.create(cloneDeep(self.filters));
    },
  }));

export const GisStore = types
  .model({
    statuses: types.optional(StatusesModel, {}),
    hasAccess: types.optional(types.boolean, false),
    guids: types.optional(types.maybeNull(types.array(GUIDModel)), null),
    records: types.optional(RecordsModel, {}),
  })
  .actions((self) => ({
    checkGISAccess: flow(function* () {
      try {
        self.statuses.checkGISAccess.loadingStates();
        const response = yield GIS.CheckGISAccess();
        self.hasAccess = response.access;
        self.statuses.checkGISAccess.successStates();

        return response;
      } catch (error) {
        console.error(error);
        self.statuses.checkGISAccess.errorStates();
      } finally {
        self.statuses.checkGISAccess.finallyStates();
      }
    }),
    getGISGUIDS: flow(function* (object_ids: string[]) {
      try {
        self.statuses.GISGUIDS.loadingStates();
        const response: IObject = yield GIS.GetGISGUIDS({
          object_id__in: object_ids,
        });
        self.guids = response.results;
        self.statuses.GISGUIDS.successStates();

        return response.results;
      } catch (e) {
        console.error(e);
        self.statuses.GISGUIDS.errorStates();
      } finally {
        self.statuses.GISGUIDS.finallyStates();
      }
    }),
    postGISGUIDS: flow(function* (meterId: string) {
      try {
        self.statuses.postGISGUIDS.loadingStates();
        const response = yield GIS.PostGISGUIDS({
          object_id: meterId,
          tag: 'HouseMeter',
        });
        self.statuses.postGISGUIDS.successStates();

        return response;
      } catch (e) {
        self.statuses.postGISGUIDS.errorStates();
        console.error(e);

        throw e;
      } finally {
        self.statuses.postGISGUIDS.finallyStates();
      }
    }),
    getGISRecords: flow(function* () {
      try {
        self.statuses.records.loadingStates();
        const response = yield GIS.GetGISRecords(
          self.records.searchFilters?.snapshot || self.records.filters.snapshot
        );
        self.records.totalCount = response.count;
        self.records.list = response.results;
        self.statuses.records.successStates();

        return response;
      } catch (e) {
        console.error(e);
        self.statuses.records.errorStates();
      } finally {
        self.statuses.records.finallyStates();
      }
    }),
    updateGISRecord: flow(function* (
      id: string,
      params: { task_state: string; task_owner: string | null }
    ) {
      const record = self.records.list.find(
        (record) => record.generated_id === id
      );

      try {
        record?.updateRecord.loadingStates();
        const response = yield GIS.UpdateGISRecord(id, params);
        record?.updateRecord.successStates();

        return response;
      } catch (e) {
        record?.updateRecord.errorStates();
        console.error(e);
      } finally {
        record?.updateRecord.finallyStates();
      }
    }),
    getErrorDetails: flow(function* (id: string) {
      try {
        return yield GIS.GetErrorDetails(id);
      } catch (e) {
        console.error(e);
      }
    }),
  }));
